import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Select,
  Space,
  Statistic,
} from 'antd';
import { PlayCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { feeApi } from '@/api/fee';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

export function FeeGeneratePage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const academicYearId = useAuthStore((s) => s.academicYearId);
  const canCreate = useAuthStore((s) => s.hasAuthority('FEE_MGMT:CREATE'));

  const [classSectionId, setClassSectionId] = useState<number | undefined>();
  const [month, setMonth] = useState<Dayjs>(dayjs().startOf('month'));

  const { data: years } = useQuery({
    queryKey: ['setup', 'academic-years'],
    queryFn: setupApi.listAcademicYears,
    staleTime: 5 * 60 * 1000,
  });
  const { data: classSections } = useQuery({
    queryKey: ['setup', 'class-sections', academicYearId ?? 'none'],
    queryFn: () => setupApi.listClassSections(academicYearId ?? undefined),
    enabled: academicYearId != null,
    staleTime: 5 * 60 * 1000,
  });
  const selectedYear = years?.find((y) => y.id === academicYearId);
  const selectedCs = classSections?.find((cs) => cs.id === classSectionId);

  const previewQuery = useQuery({
    queryKey: ['fee', 'generate-monthly', 'preview', classSectionId, month.format('YYYY-MM')],
    queryFn: () =>
      feeApi.previewMonthly({
        classSectionId: classSectionId!,
        periodMonth: month.startOf('month').format('YYYY-MM-DD'),
      }),
    enabled: classSectionId != null,
    staleTime: 30 * 1000,
  });

  const generate = useMutation({
    mutationFn: () =>
      feeApi.generateMonthly({
        classSectionId: classSectionId!,
        periodMonth: month.startOf('month').format('YYYY-MM-DD'),
      }),
    onSuccess: (res) => {
      message.success(
        `Generated ${res.invoicesCreated} invoice${res.invoicesCreated === 1 ? '' : 's'} (${res.invoicesSkipped} already existed) for ${res.enrollmentsTouched} enrollments`,
      );
      qc.invalidateQueries({ queryKey: ['fee'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const ready = classSectionId != null;
  const willCreate = previewQuery.data?.wouldCreate ?? 0;

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Fee"
        title="Generate invoices"
        description="Create one invoice per fee type per active enrollment for the selected month. Already-generated invoices are skipped, so re-running is safe."
      />

      <Card style={{ maxWidth: 720 }}>
        <Form layout="vertical">
          <Form.Item label="Class · Section">
            <Select
              placeholder="Pick a class · section"
              value={classSectionId}
              onChange={setClassSectionId}
              options={(classSections ?? [])
                .slice()
                .sort(
                  (a, b) =>
                    a.classSortOrder - b.classSortOrder ||
                    a.sectionName.localeCompare(b.sectionName),
                )
                .map((cs) => ({
                  value: cs.id,
                  label: `${cs.className} · ${cs.sectionName}`,
                }))}
              showSearch
              optionFilterProp="label"
              style={{ maxWidth: 360 }}
            />
          </Form.Item>

          <Form.Item label="Month">
            <DatePicker.MonthPicker
              value={month}
              onChange={(m) => m && setMonth(m.startOf('month'))}
              format="MMM YYYY"
              allowClear={false}
              style={{ maxWidth: 220 }}
            />
          </Form.Item>
        </Form>

        {!ready ? (
          <Empty description="Pick a class · section to preview generation" style={{ margin: '12px 0' }} />
        ) : (
          <Card
            size="small"
            title={`Preview · ${selectedCs?.className ?? '—'} · ${selectedCs?.sectionName ?? '—'} · ${selectedYear?.name ?? '—'} · ${month.format('MMM YYYY')}`}
            loading={previewQuery.isFetching}
            style={{ marginBottom: 16 }}
          >
            {previewQuery.data && (
              <Space orientation="vertical" size={12} style={{ width: '100%' }}>
                <Space size="large" wrap>
                  <Statistic
                    title="Active enrollments"
                    value={previewQuery.data.activeEnrollments}
                  />
                  <Statistic
                    title="Monthly fee types"
                    value={previewQuery.data.monthlyFeeTypes}
                  />
                  <Statistic
                    title="Will create"
                    value={previewQuery.data.wouldCreate}
                    styles={{
                      content: { color: willCreate > 0 ? '#16a34a' : undefined },
                    }}
                  />
                  <Statistic title="Already exist" value={previewQuery.data.alreadyExists} />
                </Space>
                {willCreate === 0 && previewQuery.data.activeEnrollments > 0 && (
                  <Alert
                    type="info"
                    showIcon
                    message={`All ${month.format('MMM YYYY')} invoices already exist`}
                    description={
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={() => setMonth(month.add(1, 'month').startOf('month'))}
                      >
                        Try {month.add(1, 'month').format('MMM YYYY')} →
                      </Button>
                    }
                  />
                )}
              </Space>
            )}
          </Card>
        )}

        {canCreate && (
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            disabled={!ready || willCreate === 0}
            loading={generate.isPending}
            onClick={() => generate.mutate()}
          >
            {willCreate > 0 ? `Generate ${willCreate} invoice${willCreate === 1 ? '' : 's'}` : 'Nothing to generate'}
          </Button>
        )}
      </Card>
    </div>
  );
}
